import { IActionContext, ICoreContext, getAsset, getUrlSync } from "@msdyn365-commerce/core";
import { CategoryPath, SimpleProduct, UnitOfMeasureConversion } from "@msdyn365-commerce/retail-proxy";
import { getInvoiceDetailsPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { ProductSearchResultExt, SimpleProductExt } from "./types";
import { toJS } from 'mobx';
import { getNonBreakoffUOM } from "./unit-conversion-utils";
import { getInitialBreakoffState } from "../components/common/product-list-order-quantity";
import { humanizeUnitOfMeasure } from "../components/unit-of-measure";
import { IAppSettingsInContext } from "../settings/app.settings.autogenerated";
import { IProductRelationTypeForProduct, SupportedRelationTypes } from "../actions/get-relation-types-by-ids";
import { isAlternativeProductsEnabledForOUN } from "../settings/app.settings.utils";
import { IProductInventoryInformation } from "../components/common/product-inventory-utils";
import { LifecycleStatus } from "./lifecycle-status";

const currencyFormatterDecimals = new Intl.NumberFormat('nb-NO', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});
const currencyFormatterNoDecimals = new Intl.NumberFormat('nb-NO', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});
const dateFormatter = new Intl.DateTimeFormat('nb-NO', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
});

/**
 * Gets the default quantity to use when buying a product.
 * @param product The product to get the default quantity for.
 * @param breakoff Whether breakoff is enabled.
 * @param defaultQuantity The default quantity to use if no other quantity is found. Defaults to 1.
 * @returns The default quantity to use for the product.
 */
export function getDefaultBuyQuantity(product: SimpleProduct, breakoff: boolean, defaultQuantity: number = 1): number {
    if (breakoff) {
        return 1 || defaultQuantity;
    }

    return product.Behavior?.MultipleOfQuantity || defaultQuantity;
}

export function formatCurrencyNOK(price: number | undefined, context?: ICoreContext, currencyCode: string = 'NOK', noDecimals?: boolean): string {
    if (price === undefined) {
        return '';
    }

    if (!context || currencyCode === 'NOK') {
        if (noDecimals) {
            return `${currencyFormatterNoDecimals.format(Math.trunc(price))} kr`;
        }

        return `${currencyFormatterDecimals.format(price)} kr`;
    } else {
        return context.cultureFormatter.formatCurrency(price, currencyCode);
    }
}

const currencyFormatters: { [currencyCode: string]: Intl.NumberFormat } = {};

function ensureCurrencyFormatter(currencyCode: string | undefined, locale: string | undefined): Intl.NumberFormat {
    if (!currencyFormatters[currencyCode || '']) {
        currencyFormatters[currencyCode || ''] = new Intl.NumberFormat(locale, {
            style: currencyCode ? 'currency' : 'decimal',
            currency: currencyCode,
            currencyDisplay: 'symbol'
        });
    }
    return currencyFormatters[currencyCode || ''];
}

export function formatCurrencyToParts(price: number | undefined, context: ICoreContext, currencyCode: string | undefined = 'NOK'): Intl.NumberFormatPart[] {
    const currencyFormatter = ensureCurrencyFormatter(currencyCode, context.request.locale);
    const parts = currencyFormatter.formatToParts(price || 0);

    if (context.request.locale === 'nb-NO' && currencyCode === 'NOK') {
        // Make sure symbol is after the number for Norwegian locale and NOK currency
        const symbolIndex = parts.findIndex(part => part.type === 'currency');
        const integerIndex = parts.findIndex(part => part.type === 'integer');
        if (symbolIndex < integerIndex) {
            const symbolPart = parts.splice(symbolIndex, 1)[0];
            parts.splice(parts.length, 0, { type: 'literal', value: ' ' }, symbolPart);

            if (parts[0].type === 'literal' && parts[0].value.trim() === '') {
                parts.shift(); // Remove leading space if it exists
            }
        }
    }

    return parts;
}

export function formatDate(date: Date | undefined): string {
    if (!date) {
        return '';
    }
    if (typeof date !== 'object' || !(date instanceof Date)) {
        date = new Date(date);
    }
    return dateFormatter.format(date);
}

/**
 * Returns a new array with unique items based on a specific property.
 * @param array The array to filter.
 * @param property The property to check for uniqueness.
 * @returns A new array with unique items.
 */
export function arrayUniqueByProperty<T>(array: T[], property: keyof T): T[] {
    return array.filter((x, i) => array.findIndex(y => y[property] === x[property]) === i);
}

export function getAssetFixed(context: ICoreContext, assetPath: string): string {
    let assetUrl = getAsset(assetPath, context.request);

    if (typeof window === "undefined") {
        return assetUrl;
    }

    const SUBMISSIONID = (window as any | undefined)?.___initialData___?._environmentVariable?.['SUBMISSIONID'];

    if (assetUrl.includes('00000-00000-00000-00000-00000') && SUBMISSIONID) {
        assetUrl = assetUrl.replace('00000-00000-00000-00000-00000', SUBMISSIONID);
    }

    return assetUrl;
}

export function cssCapitalize(str: string | undefined, locale: string | undefined = undefined): string | undefined {
    if (str === undefined) {
        return undefined;
    }
    return str.toLocaleLowerCase(locale).replace(
        /(^|[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^`{|}~])(\p{L})/gu,
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        (_, prefix, char) => prefix + char.toLocaleUpperCase(locale)
    );
}

export function isProductInCategory(product: SimpleProductExt | ProductSearchResultExt | undefined, categoryRecordIds: number[]): boolean {
    if (!product?.CategoryPaths || !categoryRecordIds || categoryRecordIds.length === 0) {
        return false;
    }

    const segments = toJS(product.CategoryPaths).filter(x => x.Segments).flatMap(path => path.Segments!);
    return categoryRecordIds.some(categoryId => segments.some(segment => segment.CategoryId === categoryId));
}

export function getHumanizedUnitOfMeasureText(context: ICoreContext, simpleProduct: SimpleProduct, unitConversions: UnitOfMeasureConversion[] | undefined, quantity: number | undefined): string {
    const friendlyUOM = simpleProduct.ExtensionProperties?.find((x) => x.Key === 'FRIENDLYUOM')?.Value?.StringValue;
    const nonBreakoffUOM = getNonBreakoffUOM(simpleProduct, unitConversions);
    const breakoff = getInitialBreakoffState(quantity || simpleProduct.Behavior?.MultipleOfQuantity || 1, simpleProduct);
    const multiplier = simpleProduct.Behavior?.MultipleOfQuantity || 1;
    const displayQuantity = breakoff.breakoff ? quantity : (quantity || 0) / multiplier;

    return breakoff.breakoff || !nonBreakoffUOM
        ? `${displayQuantity} ${humanizeUnitOfMeasure(context, friendlyUOM || simpleProduct.DefaultUnitOfMeasure, simpleProduct)}`
        : `${displayQuantity} ${humanizeUnitOfMeasure(context, nonBreakoffUOM, simpleProduct)}`;
}

/**
 * Returns the type of view alternative products should be shown as based on the relation types for the product and its availability.
 * @param context The core context of the application.
 * @param relationTypesForProduct The relation types for the product.
 * @param availability The availability information for the product. Leave undefined to not consider availability when determining whether to show alternative products.
 * @returns The type of view alternative products should be shown as. Returns 'Primary', 'Secondary', or undefined if alternative products should not be shown.
 */
export function getShowAlternativeProductsType(
    context: ICoreContext<IAppSettingsInContext>,
    relationTypesForProduct: IProductRelationTypeForProduct[] | undefined,
    availability: IProductInventoryInformation | boolean | undefined
): 'Primary' | 'Secondary' | undefined {
    // Should be shown if the feature is turned on for the OUN and there are relation types for the product.
    const showAlternativeProducts = isAlternativeProductsEnabledForOUN(context) &&
        relationTypesForProduct &&
        relationTypesForProduct.length > 0;

    if (!showAlternativeProducts) {
        return undefined;
    }

    const hasHardReplacement = relationTypesForProduct?.some(x => x.Name === SupportedRelationTypes.ReplacementHard);

    if (hasHardReplacement && (typeof availability === 'boolean' ? !availability : availability && availability.StockLevelCode === 'OOS')) {
        return 'Primary';
    }

    return 'Secondary';
}

export const getProductPageUrlWithCategoryPathSync = (
    actionContext: IActionContext,
    product: { RecordId: number | undefined, Name?: string } | undefined,
    categoryPaths: CategoryPath[] | undefined
) => {
    const categories = categoryPaths?.[0]?.Segments?.slice(1).map(x => ({ Name: x.CategoryName!, RecordId: x.CategoryId! })); // Skip root category

    return getUrlSync('product', actionContext, {
        product: { Name: product?.Name || '', RecordId: product?.RecordId || Number.MIN_VALUE },
        categories: categories || []
    });
};

export const isAvailableToBuy = (
    context: ICoreContext,
    product: SimpleProduct | undefined,
    showAlternativeProductsType: 'Primary' | 'Secondary' | undefined,
    availability: IProductInventoryInformation | boolean | undefined
): boolean => {
    const lifeCycleStatus = product?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
    const isAuthenticated = context.request.user.isAuthenticated;
    const isAvailableInStock = typeof availability === 'boolean' ? availability : availability?.StockLevelCode !== 'OOS';
    const isOutOfSeason = lifeCycleStatus === LifecycleStatus.OutOfSeason;
    const willExpire = lifeCycleStatus === LifecycleStatus.WillExpire;
    const isDisallowed = product?.Behavior?.IsSaleAtPhysicalStoresAllowed === false || product?.Behavior?.IsSaleAtSelfCheckoutRegistersAllowed === false;
    const requiresStockToBuy = isOutOfSeason || willExpire || showAlternativeProductsType === 'Primary';

    // Only allowed to buy when:
    // - User is authenticated
    // - Sale is allowed for the product
    // - Product is in stock when any special availability rule applies:
    //   - showAlternativeProductsType is 'Primary'
    //   - lifecycle status is OutOfSeason
    //   - lifecycle status is WillExpire
    return isAuthenticated && !isDisallowed && (!requiresStockToBuy || isAvailableInStock);
};

export const getInvoiceDetailsPageUrlFixed = (invoiceId: string, context: ICoreContext): string => {
    let urlToInvoice = getInvoiceDetailsPageUrlSync(invoiceId, context.actionContext);

    // getInvoiceDetailsPageUrlSync does not preserve domain query param from current page and will result in 404 if required
    const parsedUrl = new URL(urlToInvoice, context.request.url.requestUrl);
    if (context.request.query?.domain && !parsedUrl.searchParams.has('domain')) {
        // Clear existing search params so we can insert domain first
        const existingSearchParams = new URLSearchParams(parsedUrl.search);
        parsedUrl.search = '';
        parsedUrl.searchParams.set('domain', context.request.query.domain);
        // Re-add existing search params after domain
        existingSearchParams.forEach((value, key) => {
            parsedUrl.searchParams.set(key, value);
        });
        urlToInvoice = parsedUrl.pathname + parsedUrl.search;
    }

    return urlToInvoice;
};